import { createFileRoute, Navigate } from '@tanstack/react-router';
import { z } from 'zod';

import { CrmLearningProfile, CrmLearningProfileAccessGate } from '@features/crm-learning-profiles';
import { firstAccessibleCrmCustomerRecordArea } from '@widgets/crm-customer-record-shell';
import { normalizeCrmCourseProfileNavigationReturnTo } from '@features/crm-course-profile';

const registrationSearchSchema = z.object({
  registrationProfileTab: z
    .enum(['general', 'documents', 'completion-form', 'payments'])
    .optional(),
  returnTo: z
    .string()
    .max(4096)
    .optional()
    .transform((value) => normalizeCrmCourseProfileNavigationReturnTo(value)),
});

export const Route = createFileRoute(
  '/$locale/_authenticated/crm/user-profiles/$userProfileId/learning/registrations/$registrationId'
)({
  component: RegistrationProfileRoute,
  validateSearch: registrationSearchSchema,
});

function RegistrationProfileRoute() {
  const navigate = Route.useNavigate();
  const { userProfileId: customerId, locale, registrationId } = Route.useParams();
  const search = Route.useSearch();

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmLearningProfileAccessGate
        customerId={customerId}
        onAccessUnavailable={(allowedAreas) => {
          const fallback = firstAccessibleCrmCustomerRecordArea(allowedAreas);
          return (
            <Navigate
              params={{ userProfileId: customerId, locale }}
              replace
              to={fallback?.route ?? '/$locale/unauthorized'}
            />
          );
        }}
      >
        {(viewerAccessScope) => (
          <CrmLearningProfile
            activeTab={search.registrationProfileTab ?? 'general'}
            customerId={customerId}
            kind="registration"
            locale={locale}
            profileId={registrationId}
            viewerAccessScope={viewerAccessScope}
            onBack={() => {
              if (search.returnTo !== undefined) {
                void navigate({ to: search.returnTo as never });
                return;
              }
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/learning',
                params: { userProfileId: customerId, locale },
                search: (current: Record<string, unknown>) => {
                  const learningSearch = { ...current };
                  delete learningSearch.registrationProfileTab;
                  return learningSearch;
                },
              });
            }}
            onOpenDocument={(documentId) => {
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/documents/$documentId',
                params: { userProfileId: customerId, documentId, locale },
                search: { learningRegistrationId: registrationId },
              });
            }}
            onOpenCompletionForm={(documentId) => {
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/learning/registrations/$registrationId/completion-form/$documentId',
                params: { userProfileId: customerId, registrationId, documentId, locale },
                search: { returnTo: search.returnTo },
              });
            }}
            onOpenPaperDocuments={() => {
              void navigate({
                to: '/$locale/crm/user-profiles/$userProfileId/documents',
                params: { userProfileId: customerId, locale },
                search: (current) => ({
                  ...current,
                  documentFileState: undefined,
                  documentPage: undefined,
                  documentQ: undefined,
                  documentRegistrationId: registrationId,
                  documentSelectedId: undefined,
                  documentSort: undefined,
                  documentStatus: undefined,
                  documentType: 'paper-completion-form',
                }),
              });
            }}
            onOpenPayment={(paymentId) => {
              void navigate({
                to: '/$locale/payments/$paymentId',
                params: { locale, paymentId },
                search: {
                  detailReturn: 'registration-profile',
                  detailReturnRegistrationCustomerId: customerId,
                  detailReturnRegistrationId: registrationId,
                  detailReturnRegistrationReturnTo: search.returnTo,
                },
              });
            }}
            onTabChange={(registrationProfileTab) => {
              void navigate({
                replace: true,
                search: (current: Record<string, unknown>) => ({
                  ...current,
                  registrationProfileTab:
                    registrationProfileTab === 'general'
                      ? undefined
                      : (registrationProfileTab as 'documents' | 'completion-form' | 'payments'),
                }),
              });
            }}
          />
        )}
      </CrmLearningProfileAccessGate>
    </main>
  );
}
